import { useCallback, useEffect, useState } from "react";
import api, { fmtDT } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, MessageCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const TYPES = [
  { v: "follow_up", label: "Follow-up Reminder" },
  { v: "payment", label: "Payment Reminder" },
  { v: "appointment", label: "Appointment Reminder" },
  { v: "lab_result", label: "Lab Result Ready" },
  { v: "discharge", label: "Discharge Care" },
];

export default function WhatsApp() {
  const [rows, setRows] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ patient_id: "", type: "follow_up", channel: "whatsapp", message: "" });

  const load = useCallback(() => api.get("/reminders").then((r) => setRows(r.data)), []);
  useEffect(() => {
    load();
    api.get("/patients").then((r) => setPatients(r.data));
  }, [load]);

  const send = async () => {
    if (!form.patient_id) return toast.error("Select a patient");
    try {
      await api.post("/reminders", form);
      toast.success(`${form.channel === "whatsapp" ? "WhatsApp" : "SMS"} sent (Simulated)`);
      setOpen(false);
      setForm({ patient_id: "", type: "follow_up", channel: "whatsapp", message: "" });
      load();
    } catch { toast.error("Failed to send"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="font-display text-2xl font-semibold">WhatsApp & SMS Reminders</h1>
          <p className="text-sm text-slate-500">Send follow-up, payment and appointment reminders directly to patients.</p>
        </div>
        <Button data-testid="new-reminder-btn" onClick={() => setOpen(true)} className="bg-green-700 hover:bg-green-800 rounded-sm">
          <MessageCircle className="w-4 h-4 mr-1" /> New Reminder
        </Button>
      </div>

      <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
        DEMO MODE — WhatsApp / SMS sending is simulated. Connect Twilio / WhatsApp Business API to enable real delivery.
      </div>

      <div className="grid grid-cols-4 gap-3">
        {["follow_up", "payment", "appointment", "lab_result"].map((t) => {
          const cnt = rows.filter((r) => r.type === t).length;
          return (
            <div key={t} className="bg-white border p-3">
              <div className="label-tiny">{t.replace("_", " ")}</div>
              <div className="font-mono text-lg mt-1">{cnt}</div>
            </div>
          );
        })}
      </div>

      <div className="bg-white border">
        <div className="p-3 border-b label-tiny">Recent Reminders</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left">
              <th className="label-tiny p-2">Sent</th>
              <th className="label-tiny">Patient</th>
              <th className="label-tiny">Mobile</th>
              <th className="label-tiny">Type</th>
              <th className="label-tiny">Channel</th>
              <th className="label-tiny">Message</th>
              <th className="label-tiny">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b align-top">
                <td className="p-2 font-mono text-xs">{fmtDT(r.sent_at)}</td>
                <td className="font-medium">{r.patient_name}</td>
                <td className="font-mono text-xs">{r.mobile}</td>
                <td><Badge variant="outline" className="rounded-sm text-[10px]">{r.type}</Badge></td>
                <td><Badge className={`rounded-sm text-[10px] ${r.channel === "whatsapp" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}`}>{r.channel}</Badge></td>
                <td className="text-xs max-w-md text-slate-600">{r.message}</td>
                <td><Badge className="bg-teal-100 text-teal-800 rounded-sm text-[10px]">{r.status}</Badge></td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan="7" className="p-6 text-center text-sm text-slate-500">No reminders yet — click "New Reminder" to send your first one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Send Reminder</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="label-tiny">Patient</label>
              <select data-testid="reminder-patient" className="w-full border border-slate-300 h-9 px-2" value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })}>
                <option value="">-- Select patient --</option>
                {patients.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.mobile} ({p.uhid})</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-tiny">Reminder Type</label>
                <select data-testid="reminder-type" className="w-full border border-slate-300 h-9 px-2" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="label-tiny">Channel</label>
                <select data-testid="reminder-channel" className="w-full border border-slate-300 h-9 px-2" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="sms">SMS</option>
                </select>
              </div>
            </div>
            <div>
              <label className="label-tiny">Message (leave blank for template)</label>
              <Textarea data-testid="reminder-message" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Custom message or leave empty to use auto-generated template" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="reminder-send" onClick={send} className="bg-green-700 hover:bg-green-800">
              <Send className="w-4 h-4 mr-1" /> Send Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
